import { appAlert } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
import React, { useState } from "react";
import * as XLSX from "xlsx";
import { displayName } from "./nameMask";

/**
 * Uploading usage files, in one place.
 *
 * This lived inline in stock_excess.js. Putting a second copy on the summary
 * screen would have meant two implementations of the file sniffing, the
 * per-source pharmacy rule and the result reporting, and they would have
 * drifted the first time one was fixed. This is the single copy.
 *
 * The sniffing matters more than it looks. An NHS dispensing extract names
 * its own pharmacy through the OCS code in the file; a PMR report does not
 * name the site at all, so the user has to say which branch it belongs to.
 * Guessing that wrong writes a whole branch's usage onto another branch, and
 * nothing downstream would ever flag it.
 */

const API = apiUrl("");
const token = () => localStorage.getItem("authToken");
const DISPENSED_COLS = ["dispensed drug", "dispensed date", "dispensed quantity"];
const fmtMonth = ym => {
  const s = String(ym || "");
  if (!/^\d{6}$/.test(s)) return s;
  const d = new Date(Number(s.slice(0, 4)), Number(s.slice(4)) - 1, 1);
  return d.toLocaleDateString("en-GB", {
    month: "short",
    year: "numeric"
  });
};

/** Read only the header row: 8KB is ample and avoids pulling a 300KB file
 *  into memory just to look at its first line. */
const sniffUsageSource = async file => {
  const name = String(file.name || "").toLowerCase();
  if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, {
        type: "array",
        sheetRows: 1
      });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const row = (XLSX.utils.sheet_to_json(ws, {
        header: 1,
        blankrows: false
      })[0] || []).map(c => String(c ?? "").replace(/\uFEFF/g, "").trim().toLowerCase());
      return DISPENSED_COLS.every(c => row.includes(c)) ? "pmr" : null;
    } catch (_) {
      return null;
    }
  }
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = () => {
      const head = String(reader.result || "").split(/\r?\n/)[0];
      const cells = head.replace(/\uFEFF/g, "").toLowerCase();
      if (/textBox1\b/.test(head) && /textBox2\b/.test(head)) return resolve("pmr");
      if (DISPENSED_COLS.every(c => cells.includes(c))) return resolve("pmr");
      if (/OCS Code/i.test(head) && /Dispensing Month/i.test(head)) return resolve("nhs");
      resolve(null);
    };
    reader.onerror = () => resolve(null);
    reader.readAsText(file.slice(0, 8192));
  });
};
export default function UsageUpload({
  groupId,
  members = [],
  masked = false,
  onUploaded
}) {
  const [files, setFiles] = useState([]);
  const [source, setSource] = useState(null);
  const [pharmacyId, setPharmacyId] = useState("");
  const [busy, setBusy] = useState(false);
  const onFilesSelected = async picked => {
    setFiles(picked);
    setSource(null);
    if (picked.length === 0) return;
    const kinds = await Promise.all(picked.map(sniffUsageSource));
    const uniq = [...new Set(kinds)];
    if (uniq.length > 1) {
      (await appAlert("Please upload NHS dispensing files and PMR usage reports separately."));
      setFiles([]);
      return;
    }
    if (uniq[0] === null) {
      (await appAlert("That doesn't look like an NHS dispensing extract or a PMR usage report."));
      setFiles([]);
      return;
    }
    setSource(uniq[0]);
  };
  const upload = async () => {
    if (busy) return;
    if (files.length === 0) {
      (await appAlert("Select file(s)"));
      return;
    }
    if (source === "pmr" && !pharmacyId) {
      (await appAlert("Choose which pharmacy this usage report is for."));
      return;
    }
    setBusy(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append("files", f));
      formData.append("groupId", groupId);
      if (source === "pmr") formData.append("pharmacyId", pharmacyId);
      const res = await fetch(`${API}/uploadUsage`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token()}`
        },
        body: formData
      });
      const result = await res.json();
      if (!res.ok) {
        (await appAlert(result.error || "Upload failed"));
        return;
      }

      // products resolved vs awaiting mapping is more use than a row count,
      // and a month the file only partly covers is left alone rather than
      // overwriting a full one - the user has to be told that or the figures
      // look wrong later
      const lines = (result.files || []).map(f => {
        if (f.error) return `${f.file}: ${f.error}`;
        if (f.format === "prescribed-dispensed-events") {
          const summary = ["prescribed", "dispensed"].map(side => {
            const counts = f.matching?.[side];
            return counts ? `    ${side}: ${counts.matched} matched, ${counts.review} need review, ${counts.pending} pending, ${counts.unmatched} without candidates` : `    ${side}: matching counts unavailable — open Usage analysis to inspect matches`;
          });
          return `${f.file}: ${f.rowsRead || f.inserted || 0} report rows retained` + `\n${summary.join("\n")}` + (f.matchingError ? `\n    Matching issue: ${f.matchingError}` : "") + (f.invalidPrescribed || f.invalidDispensed ? `\n    Invalid rows excluded from totals: ${f.invalidPrescribed || 0} prescribed / ${f.invalidDispensed || 0} dispensed` : "");
        }
        if (f.source === "pmr" && f.format === "dispensed-events") {
          const months = (f.coverage || []).map(c => {
            const m = fmtMonth(c.month);
            if (c.action === "skip") return `    ${m}: NOT IMPORTED - ${c.note}`;
            const part = c.action === "insert-partial" ? " (part month only)" : "";
            return `    ${m}: ${Math.round(c.units).toLocaleString()} units` + ` from ${c.events.toLocaleString()} dispensings${part}`;
          });
          return `${f.file}: ${f.productsResolved || 0} products mapped` + (f.unmatchedProducts ? `, ${f.unmatchedProducts} awaiting mapping` : "") + (months.length ? `\n${months.join("\n")}` : "");
        }
        if (f.source === "pmr") {
          const months = (f.period?.months || []).join(", ");
          return `${f.file}: ${f.productsResolved || 0} products mapped` + (f.unmatchedPips ? `, ${f.unmatchedPips} awaiting mapping` : "") + (months ? ` (${months})` : "");
        }
        return `${f.file}: ${f.inserted || 0} rows`;
      });
      (await appAlert(`Upload complete. Inserted: ${result.inserted || 0}. ` + `Skipped files: ${result.skippedFiles || 0}.` + (lines.length ? `\n\n${lines.join("\n")}` : "")));
      setFiles([]);
      setSource(null);
      setPharmacyId("");
      if (onUploaded) onUploaded();
    } catch (err) {
      (await appAlert(`Upload error: ${err.message}`));
    } finally {
      setBusy(false);
    }
  };
  return <div>
      <input type="file" multiple onChange={e => onFilesSelected(Array.from(e.target.files || []))} />

      {source === "nhs" && <div style={{
      marginTop: 8,
      color: "#555"
    }}>
          NHS dispensing data &mdash; pharmacy read from the OCS code in the file
        </div>}

      {source === "pmr" && <div style={{
      marginTop: 8
    }}>
          <label style={{
        marginRight: 6
      }}>Pharmacy:</label>
          <select value={pharmacyId} onChange={e => setPharmacyId(e.target.value)}>
            <option value="">Select&hellip;</option>
            {/* no ocsCode filter: pharmacyId is the key, the OCS code is a label */}
            {members.map(m => <option key={m.pharmacyId} value={m.pharmacyId}>
                {displayName(m.pharmacyName, masked)}{m.ocsCode ? ` (${m.ocsCode})` : ""}
              </option>)}
          </select>
          <div style={{
        marginTop: 4,
        color: "#a00",
        fontSize: "0.9em"
      }}>
            PMR reports don't name the site, so this has to be set.
          </div>
        </div>}

      <button onClick={upload} disabled={busy || files.length === 0 || source === "pmr" && !pharmacyId} style={{
      marginTop: 10,
      padding: "7px 14px",
      fontWeight: 600
    }}>
        {busy ? "Uploading..." : "Upload"}
      </button>
    </div>;
}
